// CodeOssPane.tsx — Code OSS for one workspace, framed from the code host.
//
// The editor is served by the workspace's Pod, which the agent controls,
// so it never shares this page's origin: the manager serves it on a
// separate code host (code.thirdeye.lab0.us on lab0). Opening it is one
// call — the manager wakes the Pod if it needs waking and hands back a
// one-time launch URL — and the iframe trades that URL for a cookie of
// the code host's own. See server/managerserver/sessions_code.go.
//
// Mounted once per workspace and kept mounted while the user flips back to
// a chat, so the editor's state and socket survive the toggle — and
// survive moving between sessions that share the workspace.
//
// The editor opens in the page's light/dark scheme (the manager sets its
// theme to match; see sessions_code.go). When the page's theme toggle
// flips, the pane relaunches in the new scheme and the frame reloads: the
// editor reads its theme once, at page load. Open files and unsaved edits
// come back with it; the editor's own reload restores them.

import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Spinner } from 'react-bootstrap';
import { workspacesApi } from '../api';
import { getColorScheme, subscribeTheme } from '../common/theme';

interface Props {
  workspaceId: string;
  visible: boolean;
}

export const CodeOssPane: React.FC<Props> = ({ workspaceId, visible }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const scheme = useSyncExternalStore(subscribeTheme, getColorScheme);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError('');
    workspacesApi
      .codeLaunch(workspaceId, scheme)
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          String(err);
        console.warn(`code oss launch failed for workspace ${workspaceId}:`, msg);
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, scheme]);

  return (
    <div
      className="flex-grow-1 flex-column"
      style={{ minHeight: 0, display: visible ? 'flex' : 'none' }}
    >
      {error ? (
        <div className="alert alert-danger m-3 py-2 small">Could not open Code OSS: {error}</div>
      ) : !url ? (
        <div className="d-flex align-items-center justify-content-center flex-grow-1 text-muted small">
          <Spinner animation="border" size="sm" className="me-2" />
          Opening Code OSS…
        </div>
      ) : (
        <iframe
          title={`Code OSS for ${workspaceId}`}
          src={url}
          // The editor copies and pastes through the async clipboard API,
          // which a cross-origin frame only gets when the parent says so.
          allow="clipboard-read; clipboard-write"
          style={{ border: 0, flexGrow: 1, width: '100%', minHeight: 0 }}
        />
      )}
    </div>
  );
};

export default CodeOssPane;
